import { Stack } from '@mantine/core';
import { schemaResolver, useForm } from '@mantine/form';
import { Panel } from 'components/Panel';
import { SaveButton } from 'components/SaveButton';
import { i18nKeys, useTranslation } from 'locales';
import { useEffect } from 'react';
import { SendingMediumSelector } from 'shared/components/SendingMedium/SendingMediumSelector';
import {
  type SendingMedium,
  SendingMediumSchema,
} from 'shared/hooks/use-load-company-configuration';
import { z } from 'zod/v4';

import { useLoadInvoiceSendingMedium, useUpdateInvoiceSendingMedium } from '../api';

type SendingMediumFormValues = {
  sendingMedium: Array<SendingMedium>;
};

const SendingMediumFormSchema = z.object({
  sendingMedium: z.array(SendingMediumSchema),
});

export const SendingMediumCard = () => {
  const { t } = useTranslation();

  const { invoiceSendingMedium } = useLoadInvoiceSendingMedium();
  const { updateInvoiceSendingMedium, isUpdateInvoiceSendingMediumLoading } =
    useUpdateInvoiceSendingMedium();

  const form = useForm<SendingMediumFormValues>({
    initialValues: { sendingMedium: [] },
    validate: schemaResolver(SendingMediumFormSchema, { sync: true }),
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: sync server-loaded media into the form only when the fetched data changes, not on every render (the form object identity is unstable)
  useEffect(() => {
    if (!invoiceSendingMedium) return;

    const values: SendingMediumFormValues = {
      sendingMedium: invoiceSendingMedium.sendingMedium.map(medium => ({
        name: medium.name,
        order: medium.order,
        force_sending: medium.forceSending,
      })),
    };

    form.setInitialValues(values);
    form.setValues(values);
  }, [invoiceSendingMedium]);

  const mediums = form.getValues().sendingMedium;

  const handleSave = () => {
    updateInvoiceSendingMedium(
      {
        sendingMedium: mediums.map((medium, index) => ({
          name: medium.name,
          order: medium.order ?? index + 1,
          forceSending: !!medium.force_sending,
        })),
      },
      { onSuccess: () => form.resetDirty() },
    );
  };

  return (
    <Panel
      title={t(i18nKeys.DEBTOR.SENDING_MEDIUM)}
      withBorder
      fullHeight
      pinFooter
      footer={
        <SaveButton
          onClick={handleSave}
          disabled={!form.isDirty() || mediums.length === 0}
          loading={isUpdateInvoiceSendingMediumLoading}
          hideIcon
        />
      }
    >
      <Stack my="md">
        <SendingMediumSelector
          mediums={mediums}
          onCreateFirst={name =>
            form.setFieldValue('sendingMedium', [{ name, order: 1, force_sending: true }])
          }
          onInsert={medium => form.insertListItem('sendingMedium', medium)}
          onRemove={index => form.removeListItem('sendingMedium', index)}
          onChangeName={(index, name) => form.setFieldValue(`sendingMedium.${index}.name`, name)}
          onChangeForceSending={(index, forceSending) =>
            form.setFieldValue(`sendingMedium.${index}.force_sending`, forceSending)
          }
        />
      </Stack>
    </Panel>
  );
};
